import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Button, Typography, Grid } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ProductPriceAvailabilityError from '@src/components/ProductPriceAvailability/ProductPriceAvailabilityError'
import {
  ProductSearchType,
  determineQueryFocus,
  formatCasNumberSearchTerm,
  determineCatalogType,
} from '@utils/searchUtils'
import { useRouter } from '@src/routes'
import LiquidQuantityInput from '@src/components/LiquidQuantityInput'
import MaterialPricingDisplay from '@src/components/MaterialPricingDisplay'
import { PricingLoading } from '@src/components/ProductQuickLook/ProductQuickLook'
import { ProductLockupSimple } from '@src/components/ProductLockupSimple/ProductLockupSimple'
import { SubstanceInfoSegment } from '@src/components/SubstanceDetailSegment'
import HandleMarkup from '@src/components/HandleMarkup'
import SDSLink from '@src/components/SDSLink'
import LabeledDownloadIcon from '@src/components/ProductInfo/LabeledDownloadIcon'
import ProductPriceAvailability from '@src/components/ProductPriceAvailability'
import { HeaderSearchAddToCartProps } from './HeaderSearchAddToCart'
import { useMaterialPricingAndAvailabilityLazyQuery } from '@src/queries/MaterialPricingAndAvailabilityQuery.generated'
import { useSdsCertificateSearchLazyQuery } from '@src/queries/SdsCertificateSearchQuery.generated'
import { useSpecificationSheetUrlLazyQuery } from '@src/queries/SpecificationSheetUrlQuery.generated'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import {
  Suggestion,
  CatalogType,
  MaterialAvailabilityKey,
} from '@src/types/graphql-types'
import {
  sendHeaderSearchEvent,
  sendProductInfoInteractionEvent,
} from '@src/utils/analytics'
import { buildProductAliasPayload } from '@src/utils/analytics/analyticsUtils'
import ProductPaMessage from '@src/components/ProductPriceAvailability/ProductPaMessage'
import { SitePreference, useCurrentUser } from '@utils/useCurrentUser'
import { useAbility, Abilities } from '@src/utils/useAbility'
import BuyNowButton from '@src/components/BuyNowButton'
import BlockedProductMessage from '@src/components/BlockedProductMessage/BlockedProductMessage'
import { MaterialNumberChips } from '@src/components/MaterialNumberChips/MaterialNumberChips'
import MaterialAvailabilityDisplay from '@src/components/MaterialAvailabilityDisplay'
import { InitialShipEstimate } from '@src/components/ProductPriceAvailability/ProductPriceAvailabilityFormBody'
import DiscontinuedProductAlert from '@src/components/DiscontinuedProductAlert'
import { sendPricingAvailabilityInteractionEvent } from '@src/utils/analytics/pricingAndAvailability'
import { TrackedAvailabilityMessageProvider } from '@src/components/TrackedAvailabilityMessage/TrackedAvailabilityMessageContext'

const useStyles = makeStyles((theme: Theme) => {
  const thumbnailOffset = theme.spacing(28)
  return {
    materialQuickLookContainer: {
      padding: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(8),
      },
    },
    infoList: {
      paddingTop: theme.spacing(4),
      maxWidth: 900,
      '& dd, & dt': { margin: 0 }, // reset browser styles
      '&:empty': { display: 'none' },
    },
    formula: {
      paddingTop: theme.spacing(1),
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
    },
    thumbnailOffset: {
      paddingLeft: thumbnailOffset,
    },
    priceContainer: {
      marginTop: theme.spacing(3),
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      height: 65, // matches height of loaded-in content
    },
    price: {
      fontSize: theme.typography.pxToRem(24),
      fontWeight: theme.typography.fontWeightBold,
    },
    listPrice: {
      fontSize: theme.typography.pxToRem(11),
      lineHeight: 1,
      textDecoration: 'line-through',
    },
    availabilityMessages: {
      paddingTop: theme.spacing(2),
      '& > *': {
        borderTop: 'none',
        padding: 0,
      },
      '& p': {
        fontSize: theme.typography.pxToRem(14),
      },
    },
    errorContainer: {
      paddingTop: theme.spacing(4),
    },
    quickLookAddContainer: {
      marginTop: theme.spacing(6),
      display: 'block',
      '& > div': {
        marginRight: theme.spacing(0),
        marginBottom: theme.spacing(6),
      },
      '& > button': {
        width: '100%',
      },
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        '& > div': {
          marginBottom: theme.spacing(0),
          marginRight: theme.spacing(4),
        },
        '& > button': {
          width: 'auto',
          marginRight: theme.spacing(4),
        },
        '& button:last-child': {
          marginRight: theme.spacing(0),
        },
      },
    },
    documentsContainer: {
      paddingLeft: thumbnailOffset,
      paddingTop: theme.spacing(10),
    },
    sectionTitle: {
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      fontSize: theme.typography.pxToRem(12),
      paddingBottom: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
    documents: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: theme.spacing(-1, -3),
      '& > *': {
        margin: theme.spacing(1, 3),
      },
    },
    productPriceAvailabilityWrapper: {
      marginTop: theme.spacing(6),
    },
    closeIcon: {
      alignSelf: 'flex-start',
      color: theme.palette.common.black,
      paddingTop: '0',
      paddingRight: '0',
      marginRight: '-4px',
      minWidth: '44px',
      '&:hover': {
        color: theme.palette.common.black,
      },
      '& > span > svg': {
        fontSize: theme.typography.pxToRem(28),
      },
    },
    productLockupContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      '& > div:first-child': {
        width: '100%',
      },
    },
    noQuickViewItem: {
      height: '300px',
    },
    liquidQtyInputContainer: {
      maxWidth: '112px',
    },
    alignLeft: {
      justifyContent: 'flex-start',
    },
    checkAvailabilityText: {
      justifyContent: 'flex-start',
      padding: 0,
      marginTop: theme.spacing(4),
      fontWeight: theme.typography.fontWeightBold,
      '&.MuiButton-root.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
  }
})

interface HeaderSearchQuickLookProps {
  headerSearchAddToCartProps: HeaderSearchAddToCartProps
  orgId: string | null | undefined
  prodNumberQuickLook?: Suggestion | null
  isSubmitDisabled: boolean
  onSubmit: (submitData?: ValidMaterialPricingDetailFragment) => void
  onClose?: () => void
  toggleMarketplaceSellerModalOpen?: () => void
  handleBuyNowClick: () => void
}

const HeaderSearchQuickLook: React.FC<HeaderSearchQuickLookProps> = ({
  headerSearchAddToCartProps,
  orgId,
  prodNumberQuickLook,
  isSubmitDisabled,
  onSubmit,
  onClose,
  toggleMarketplaceSellerModalOpen,
  handleBuyNowClick,
}) => {
  const { authorized: canAddToCart } = useAbility(Abilities.canAddToCart)
  const classes = useStyles()
  const router = useRouter()
  const { formatMessage } = useIntl()
  const { currentUser, getSitePreference } = useCurrentUser()
  const hideAddToCartForPrepackItems = getSitePreference(
    SitePreference.HideAddToCartForPrepackItems
  )
  const [fetchAvailabilityData, { loading, error, data }] =
    useMaterialPricingAndAvailabilityLazyQuery()
  const [selectedProductMaterial, setSelectedProductMaterial] = useState('')
  const [loadSds, { data: sdsData }] = useSdsCertificateSearchLazyQuery()
  const [loadSpecSheet, { data: specSheetData }] =
    useSpecificationSheetUrlLazyQuery()

  const { materialPricing, discontinuedPricingInfo } =
    data?.getPricingForMaterial || {}
  const [availability] = materialPricing?.availabilities || []
  const isBlockedProduct = materialPricing?.isBlockedProduct
  const { material, product, quantity, setQuantity } =
    headerSearchAddToCartProps
  const materialNumber = material?.number
  const { focus = '', catalog = '' } = router.query || {}

  const [productAvailabilityQuantity, setProductAvailabilityQuantity] =
    useState(1)

  const showAddToCart =
    ((!error && !material?.isMarketplace) ||
      (!error &&
        material?.isMarketplace &&
        currentUser?.metadata.isMarketplaceCartEnabled)) &&
    !hideAddToCartForPrepackItems

  const inquireForPAndA =
    availability?.key === MaterialAvailabilityKey.InquireForPriceAndAvailability

  // TODO: Fix the lint issue, Needs to be refactored to fix react hooks lint issues.
  // Will follow up on this later as a tech debt item.

  useEffect(() => {
    if (materialNumber) {
      const marketplace = !!material?.isMarketplace
        ? CatalogType.Marketplace
        : null

      fetchAvailabilityData({
        variables: {
          materialNumber,
          quantity,
          catalogType: determineCatalogType(marketplace || focus || catalog),
          orgId,
          checkBuyNow: true,
        },
      })
    }
  }, [
    fetchAvailabilityData,
    materialNumber,
    orgId,
    productAvailabilityQuantity,
  ])

  const quickViewItem = material ? material : product
  const brandKey = quickViewItem?.brand.key
  const productKey = material
    ? material.product
    : product?.productKey
      ? product.productKey
      : ''
  const productNumber = material ? material.product : product?.productNumber

  useEffect(() => {
    setProductAvailabilityQuantity(1)
    setQuantity(1)
  }, [quickViewItem?.id, setQuantity])

  // TODO: Fix the lint issue, Needs to be refactored to fix react hooks lint issues.
  // Will follow up on this later as a tech debt item.

  useEffect(() => {
    if (!productNumber || !brandKey) return

    sendPricingAvailabilityInteractionEvent({
      action: 'view p&a',
      section: 'header search',
      component: 'autosuggest',
      material: {
        materialDescription: material
          ? material.substanceKey || undefined
          : product?.substance.key,
        brand: brandKey,
        product: product?.productNumber || material?.number,
      },
    })
    loadSds({
      variables: {
        productNumber: productNumber,
        brand: brandKey,
      },
    })
    loadSpecSheet({
      variables: {
        input: {
          productNumber: productNumber,
          brand: brandKey,
        },
      },
    })
  }, [loadSds, productKey, loadSpecSheet])

  if (
    !quickViewItem ||
    (prodNumberQuickLook &&
      product?.productKey !== prodNumberQuickLook?.productKey)
  )
    return <div className={classes.noQuickViewItem} />

  const productFormula =
    quickViewItem.linearFormula || quickViewItem.empiricalFormula

  const quickViewName = quickViewItem.name || ''

  const sdsLanguages =
    sdsData?.getSdsCertificateSearch?.sds[0]?.languages.altLanguages || []

  const quickViewNumber = material?.number || product?.productNumber || ''

  const isCheckAvailabilityDisabled = quantity === productAvailabilityQuantity

  const quickViewImage = material
    ? {
        mediumUrl: material.image.mediumUrl,
      }
    : product
      ? {
          mediumUrl: product.images[0] && product.images[0].mediumUrl,
        }
      : {
          mediumUrl: '',
        }

  return (
    quickViewItem && (
      <div className={classes.materialQuickLookContainer}>
        <div className={classes.productLockupContainer}>
          <ProductLockupSimple
            image={quickViewImage}
            brand={quickViewItem.brand}
            productKey={productKey}
            productNumber={quickViewNumber}
            name={quickViewName}
            displaySellerName={quickViewItem.displaySellerName || ''}
            description={quickViewItem.description}
            toggleMarketplaceSellerModalOpen={toggleMarketplaceSellerModalOpen}
            showPreviouslyPurchasedChip
            additionalInfo={
              <Grid
                container
                component="dl"
                spacing={1}
                className={classes.infoList}
                alignItems="baseline"
              >
                {productFormula && (
                  <>
                    <Grid item xs={5} lg={2}>
                      <Typography variant="caption" component="dt">
                        {quickViewItem.linearFormula ? (
                          <FormattedMessage
                            id="LINEAR_FORMULA"
                            defaultMessage="Linear Formula"
                          />
                        ) : (
                          <FormattedMessage
                            id="EMPIRICAL_FORMULA"
                            defaultMessage="Empirical Formula"
                          />
                        )}
                        :
                      </Typography>
                    </Grid>
                    <Grid item xs={7} lg={10}>
                      <dd className={classes.formula}>
                        <HandleMarkup value={productFormula} />
                      </dd>
                    </Grid>
                  </>
                )}
                {quickViewItem.casNumber && (
                  <>
                    <Grid item xs={5} lg={2} component="dt">
                      <Typography variant="caption" component="div">
                        <FormattedMessage
                          id="CAS_NUMBER"
                          defaultMessage="CAS Number"
                        />
                        :
                      </Typography>
                    </Grid>
                    <Grid item xs={7} lg={10} component="dd">
                      <SubstanceInfoSegment
                        color="primary"
                        label={quickViewItem.casNumber}
                        searchValue={formatCasNumberSearchTerm(
                          quickViewItem.casNumber
                        )}
                        searchFocusType={determineQueryFocus(focus)}
                        productSearchType={ProductSearchType.CasNumber}
                        onClick={() => {
                          sendHeaderSearchEvent({
                            searchTerm: quickViewNumber,
                            searchAutoSuggest: 'quick view - cas number',
                            searchAutoSuggestTerm: `${quickViewItem.casNumber}`,
                            searchType: determineQueryFocus(focus),
                            searchComponent: 'global search',
                            searchErrorMessage: '',
                          })
                          sendProductInfoInteractionEvent(
                            buildProductAliasPayload({
                              action: 'cas number',
                              detail: quickViewItem.casNumber,
                              focus,
                            })
                          )
                        }}
                      />
                    </Grid>
                  </>
                )}
                {quickViewItem.molecularWeight && (
                  <>
                    <Grid item xs={5} lg={2} component="dt">
                      <Typography variant="caption" component="div">
                        <FormattedMessage
                          id="MOLECULAR_WEIGHT"
                          defaultMessage="Molecular Weight"
                        />
                        :
                      </Typography>
                    </Grid>
                    <Grid item xs={7} lg={10} component="dd">
                      <HandleMarkup value={quickViewItem.molecularWeight} />
                    </Grid>
                  </>
                )}
              </Grid>
            }
            isMarketplace={!!quickViewItem?.isMarketplace}
            marketplaceSellerId={
              quickViewItem?.marketplaceSellerId || undefined
            }
          />
          <Button
            aria-label="close"
            className={classes.closeIcon}
            onClick={() => {
              onClose && onClose()
              sendPricingAvailabilityInteractionEvent({
                action: 'close p&a',
                section: 'quick view p&a',
                component: 'modal',
                elementType: 'icon',
                material: {
                  materialDescription: material
                    ? material.substanceKey || undefined
                    : product?.substance.key,
                  brand: brandKey,
                  product: product?.productNumber || material?.number,
                },
              })
            }}
          >
            <CloseIcon />
          </Button>
        </div>
        {material && (
          <div
            className={clsx(
              classes.thumbnailOffset,
              classes.productPriceAvailabilityWrapper
            )}
          >
            <div className={classes.priceContainer}>
              {loading && (
                <div className={classes.loadingContainer}>
                  <PricingLoading />
                </div>
              )}
              {error && (
                <div className={classes.errorContainer}>
                  <TrackedAvailabilityMessageProvider source="header search">
                    <ProductPriceAvailabilityError
                      error={error}
                      productNumber={material.number}
                      isMarketplace={!!quickViewItem?.isMarketplace}
                      sellerName={quickViewItem?.displaySellerName || ''}
                      marketplaceSellerId={
                        quickViewItem?.marketplaceSellerId || undefined
                      }
                      marketplaceOfferId={
                        quickViewItem?.marketplaceOfferId || undefined
                      }
                    />
                  </TrackedAvailabilityMessageProvider>
                </div>
              )}
              {materialPricing && (
                <>
                  {!inquireForPAndA && (
                    <MaterialPricingDisplay
                      material={materialPricing}
                      priceClass={classes.price}
                      listPriceClass={classes.listPrice}
                    />
                  )}
                  <TrackedAvailabilityMessageProvider
                    source="header search"
                    brand={brandKey}
                    item={material.number}
                    pricing={materialPricing}
                  >
                    <MaterialAvailabilityDisplay
                      hideDetailsLink
                      canAddToCart={canAddToCart}
                      material={materialPricing}
                      initialShipEstimate={
                        <InitialShipEstimate
                          availability={availability}
                          quantity={productAvailabilityQuantity}
                        />
                      }
                      gaSection={'quick view p&a'}
                      gaComponent={'modal'}
                    />
                  </TrackedAvailabilityMessageProvider>
                  {showAddToCart && !isBlockedProduct && !inquireForPAndA ? (
                    <>
                      <div className={classes.quickLookAddContainer}>
                        {canAddToCart && (
                          <>
                            <div className={classes.liquidQtyInputContainer}>
                              <LiquidQuantityInput
                                value={quantity}
                                min={1}
                                onChange={(value) => {
                                  setQuantity(value)
                                }}
                              />
                            </div>
                            <Button
                              color="secondary"
                              variant="contained"
                              size="large"
                              disabled={isSubmitDisabled}
                              onClick={() => onSubmit(materialPricing)}
                            >
                              {formatMessage({
                                id: 'ADD_TO_CART',
                                defaultMessage: 'Add to Cart',
                              })}
                            </Button>
                            {materialPricing.isBuyNow && (
                              <BuyNowButton
                                materialNumber={materialNumber || ''}
                                quantity={Number(quantity) || 1}
                                onSuccess={handleBuyNowClick}
                                marketplace={!!quickViewItem?.isMarketplace}
                                gaType="header search"
                              />
                            )}
                          </>
                        )}
                      </div>
                      <Button
                        variant="text"
                        color="primary"
                        size="medium"
                        disabled={isCheckAvailabilityDisabled}
                        onClick={() => setProductAvailabilityQuantity(quantity)}
                        className={classes.checkAvailabilityText}
                      >
                        <FormattedMessage
                          id="CHECK_AVAILABILITY_BUTTON"
                          defaultMessage="Check Availability"
                        />
                      </Button>
                    </>
                  ) : null}

                  {isBlockedProduct ? <BlockedProductMessage /> : null}
                </>
              )}
              {discontinuedPricingInfo ? (
                <DiscontinuedProductAlert
                  discontinuedPricingInfo={discontinuedPricingInfo}
                  leftAligned
                />
              ) : null}
            </div>
          </div>
        )}
        {prodNumberQuickLook && !material && (
          <div
            className={clsx(
              classes.thumbnailOffset,
              classes.productPriceAvailabilityWrapper
            )}
          >
            {'paMessage' in quickViewItem && quickViewItem.paMessage ? (
              <ProductPaMessage
                brandKey={quickViewItem.brand.key}
                productNumber={quickViewItem.productNumber}
                paMessage={quickViewItem.paMessage}
              />
            ) : (
              <TrackedAvailabilityMessageProvider source="header search">
                <ProductPriceAvailability
                  productNumber={quickViewNumber}
                  quantity={productAvailabilityQuantity}
                  brandKey={brandKey}
                  displaySellerName={product?.displaySellerName || ''}
                  isMarketplace={product?.isMarketplace}
                  marketplaceSellerId={product?.marketplaceSellerId || ''}
                  marketplaceOfferId={product?.marketplaceOfferId || ''}
                  productAttributes={product?.attributes}
                  LoadingComponent={PricingLoading}
                  productName={product?.name || ''}
                  PricingComponent={({ materialPricing }) => {
                    const selectedMaterialPricing = selectedProductMaterial
                      ? materialPricing.find(
                          (item) =>
                            item.packageSize === selectedProductMaterial ||
                            item.materialNumber === selectedProductMaterial
                        )
                      : materialPricing[0]
                    const [selectedMaterialAvailability] =
                      selectedMaterialPricing?.availabilities || []
                    const selectedMaterialInquireForPAndA =
                      selectedMaterialAvailability?.key ===
                      MaterialAvailabilityKey.InquireForPriceAndAvailability
                    if (!selectedMaterialPricing) return null
                    return (
                      <>
                        <Typography
                          variant="caption"
                          component="h2"
                          className={classes.sectionTitle}
                        >
                          <FormattedMessage id="SKU" defaultMessage="SKU" />
                        </Typography>
                        <MaterialNumberChips
                          materialPricing={materialPricing}
                          selectedChip={selectedMaterialPricing.materialNumber}
                          onSelect={setSelectedProductMaterial}
                        />
                        <div className={classes.priceContainer}>
                          {!selectedMaterialInquireForPAndA && (
                            <MaterialPricingDisplay
                              material={selectedMaterialPricing}
                              priceClass={classes.price}
                              listPriceClass={classes.listPrice}
                            />
                          )}
                          <TrackedAvailabilityMessageProvider
                            source="header search"
                            brand={brandKey}
                            item={selectedMaterialPricing.materialNumber}
                            pricing={selectedMaterialPricing}
                          >
                            <MaterialAvailabilityDisplay
                              canAddToCart={canAddToCart}
                              material={selectedMaterialPricing}
                              initialShipEstimate={
                                <InitialShipEstimate
                                  availability={selectedMaterialAvailability}
                                  quantity={productAvailabilityQuantity}
                                />
                              }
                              hideDetailsLink
                              gaSection={'quick view p&a'}
                              gaComponent={'modal'}
                            />
                          </TrackedAvailabilityMessageProvider>
                        </div>
                        {((selectedMaterialPricing &&
                          !quickViewItem?.isMarketplace) ||
                          (selectedMaterialPricing &&
                            quickViewItem?.isMarketplace &&
                            currentUser?.metadata.isMarketplaceCartEnabled)) &&
                        !hideAddToCartForPrepackItems &&
                        !selectedMaterialPricing.isBlockedProduct &&
                        !selectedMaterialInquireForPAndA ? (
                          <div className={classes.quickLookAddContainer}>
                            {canAddToCart && (
                              <>
                                <div
                                  className={classes.liquidQtyInputContainer}
                                >
                                  <LiquidQuantityInput
                                    value={quantity}
                                    min={1}
                                    onChange={setQuantity}
                                  />
                                </div>

                                <Button
                                  color="secondary"
                                  variant="contained"
                                  size="large"
                                  disabled={
                                    isSubmitDisabled || !materialPricing.length
                                  }
                                  onClick={() => {
                                    onSubmit(selectedMaterialPricing)
                                  }}
                                >
                                  {formatMessage({
                                    id: 'ADD_TO_CART',
                                    defaultMessage: 'Add to Cart',
                                  })}
                                </Button>
                                {selectedMaterialPricing.isBuyNow && (
                                  <BuyNowButton
                                    materialNumber={
                                      selectedMaterialPricing?.materialNumber ||
                                      ''
                                    }
                                    quantity={Number(quantity) || 1}
                                    marketplace={!!quickViewItem?.isMarketplace}
                                    gaType="header search"
                                  />
                                )}
                              </>
                            )}
                          </div>
                        ) : null}
                        <Button
                          variant="text"
                          color="primary"
                          size="medium"
                          onClick={() => {
                            setProductAvailabilityQuantity(quantity)
                            sendPricingAvailabilityInteractionEvent({
                              action: 'check product availability',
                              section: 'quick view p&a',
                              component: 'modal',
                              elementType: 'link',
                              elementText: 'check availability',
                              material: selectedMaterialPricing,
                            })
                          }}
                          disabled={isCheckAvailabilityDisabled}
                          className={classes.checkAvailabilityText}
                        >
                          <FormattedMessage
                            id="CHECK_AVAILABILITY_BUTTON"
                            defaultMessage="Check Availability"
                          />
                        </Button>
                        {selectedMaterialPricing?.isBlockedProduct ? (
                          <BlockedProductMessage />
                        ) : null}
                      </>
                    )
                  }}
                  usePricingComponentOnMobile
                  condensed
                />
              </TrackedAvailabilityMessageProvider>
            )}
          </div>
        )}
        {(sdsLanguages.length > 0 ||
          specSheetData?.getSpecificationSheet?.url) && (
          <div className={classes.documentsContainer}>
            <Typography
              variant="caption"
              component="h2"
              className={classes.sectionTitle}
            >
              <FormattedMessage id="DOCUMENTS" defaultMessage="Documents" />
            </Typography>
            <div className={classes.documents}>
              {sdsLanguages.length > 0 && (
                <SDSLink
                  // TODO: The SDS modal doesn't work well with the search dropdown
                  // (both technically and design-wise). Revisit the UX to support
                  // multiple SDS languages.
                  sdsLanguages={[sdsLanguages[0]]}
                  productNumber={productNumber || ''}
                  brandKey={brandKey || ''}
                  trigger={<LabeledDownloadIcon />}
                  onClick={() => sendHeaderSearchEvent()}
                />
              )}
              {specSheetData?.getSpecificationSheet?.url && (
                <a
                  href={specSheetData.getSpecificationSheet.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={() => sendHeaderSearchEvent()}
                >
                  <LabeledDownloadIcon label="SPECIFICATION_SHEET" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    )
  )
}

export default HeaderSearchQuickLook
